// libs.
import { toast } from "react-toastify";
import { useEffect, useState } from "react";

// store.
import { useUserLeadDetail } from "@shared/hooks/userLeadDetail.hook";
import { useGetBonusInfoQuery } from "@store/apiSlices/bonus/services/getBonusInfo.endpoints";
import { useAddBonusInfoMutation } from "@store/apiSlices/bonus/services/addBonusInfo.endpoints";

// components.
import { BonusModal } from "./BonusModal";
import { BonusTimer } from "./BonusTimer";
import { formatPrice } from "@utils";
import { normalizedPercentage, toDisplayPercentage } from "./utils";
import { BonusInfoItem } from "@store/apiSlices/bonus/interfaces/interfaces";

export enum BonusOpType {
  CREATE = "C",
  UPDATE = "U",
  DELETE = "D",
}

const bonusExists = (bonusInfo?: BonusInfoItem) =>
  !!bonusInfo &&
  bonusInfo.op_type !== "sin_datos" &&
  bonusInfo.error !== "sin_bono";

const bonusWasDeleted = (bonusInfo?: BonusInfoItem) =>
  !!bonusInfo && bonusInfo.op_type === BonusOpType.DELETE;

const getBonusLabel = ({
  bonusInfo,
  existsBonus,
  isLoadingBonus,
}: {
  bonusInfo?: BonusInfoItem;
  existsBonus: boolean;
  isLoadingBonus: boolean;
}) => {
  if (isLoadingBonus) return "Cargando…";
  if (existsBonus) {
    return `${toDisplayPercentage(bonusInfo?.percentage ?? 0)}% - ${formatPrice(bonusInfo?.amount ?? 0)}`;
  }
  return "Agregar bono";
};

export const BonusCard = ({
  minPublicationPrice,
  publicationPrice,
}: {
  minPublicationPrice: number;
  publicationPrice: number;
}) => {
  const [show, setShow] = useState(false);

  const { nid, country, email } = useUserLeadDetail();
  const [addBonusInfo, { isLoading, isSuccess, isError }] =
    useAddBonusInfoMutation();

  const {
    data: bonusInfo,
    refetch,
    isLoading: isLoadingBonus,
    isError: isErrorBonus,
  } = useGetBonusInfoQuery({
    country: country ?? "",
    nid: nid ?? "",
  });

  const existsBonus = bonusExists(bonusInfo);
  const isBonusDeleted = bonusWasDeleted(bonusInfo);

  const handleAddBonus = (
    comment: string,
    percentage?: number,
    amount?: number,
  ) => {
    const opType = existsBonus ? BonusOpType.UPDATE : BonusOpType.CREATE;

    addBonusInfo({
      amount: amount ?? 0,
      country: country ?? "",
      meta: { comentario: comment },
      min_publication_price: minPublicationPrice,
      nid: Number(nid),
      op_type: opType,
      percentage: normalizedPercentage(percentage ?? 0),
      user_id: email,
    });
  };

  useEffect(() => {
    if (isLoadingBonus) {
      toast.loading("Cargando bono...", { toastId: "loading-bonus" });
    } else {
      toast.dismiss("loading-bonus");
    }
    if (isErrorBonus) {
      toast.error("Error al cargar bono");
    }
  }, [isLoadingBonus, isErrorBonus]);

  useEffect(() => {
    if (isSuccess) {
      toast.success("Bono creado/actualizado correctamente");
      setShow(false);
      refetch();
    }
    if (isError) {
      toast.error("Error al crear/actualizar bono");
    }
  }, [isSuccess, isError]);

  return (
    <>
      {isBonusDeleted && bonusInfo ? (
        <BonusTimer validUntil={bonusInfo.valid_until} />
      ) : (
        <div
          className="d-flex justify-content-between py-2 mt-2 bonus-card"
          onClick={isLoadingBonus ? undefined : () => setShow(true)}
          style={
            isLoadingBonus ? { cursor: "default", opacity: 0.6 } : undefined
          }
        >
          <div className="fw-bold">Bono:</div>
          <div>{getBonusLabel({ isLoadingBonus, existsBonus, bonusInfo })}</div>
        </div>
      )}
      <BonusModal
        existsBonus={existsBonus}
        bonusInfo={bonusInfo}
        country={country as "CO" | "MX"}
        isLoading={isLoading}
        minPublicationPrice={minPublicationPrice}
        publicationPrice={publicationPrice}
        onConfirm={handleAddBonus}
        onClose={() => setShow(false)}
        show={show}
        title={existsBonus ? "Editar bono" : "Agregar bono"}
      />
    </>
  );
};
